import { Image as ImageIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ReadTemplateImage } from '@/services/api/generated/templatesV2Service';
import {
  useTemplateImageRemapMutation,
  useTemplateImagesQuery,
} from '@/services/api/generated/templatesV2Service';
import { useTemplateContextFieldsQuery } from '@/services/api/templatesV2ServiceEnhanced';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TemplateImagesPanelProps {
  templateId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// ImageLightbox
// ---------------------------------------------------------------------------

const ImageLightbox = ({
  image,
  open,
  onOpenChange,
}: {
  image: ReadTemplateImage | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  if (!image) return null;
  const filename = image.rel_path.split('/').pop() ?? image.rel_path;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden bg-zinc-950">
        <DialogTitle className="sr-only">{filename}</DialogTitle>
        <div className="flex items-center justify-center p-2 min-h-[200px]">
          <img
            src={image.file_url ?? ''}
            alt={filename}
            className="max-h-[70vh] max-w-full object-contain"
          />
        </div>
        <div className="flex items-center gap-3 border-t border-zinc-800 px-4 py-2.5 text-xs text-zinc-400">
          <span className="truncate font-mono font-medium">{filename}</span>
          <span className="shrink-0">{image.width}&times;{image.height}</span>
          <span className="shrink-0">{formatBytes(image.size_bytes)}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// ImageItem
// ---------------------------------------------------------------------------

const NONE_VALUE = '__none__';

/** Keys to exclude from the image mapping dropdown. */
const EXCLUDED_IMAGE_KEYS = new Set(['clientHipaaSigUrl']);

/** Friendlier display names for image context fields. */
const FRIENDLY_IMAGE_LABELS: Record<string, string> = {
  firmParalegalSig: 'Paralegal Signature',
  firmLetterheadImageUrl: 'Firm Letterhead',
  firmFooterImageUrl: 'Firm Footer',
  firmLogoImageUrl: 'Firm Logo',
  firmWatermarkImageUrl: 'Firm Watermark',
  attorneySig: 'Attorney Signature',
  clientHipaaCleanedSigUrl: 'Client HIPAA Signature',
};

const ImageItem = ({
  image,
  templateId,
  imageContextOptions,
  isLast,
  onPreview,
}: {
  image: ReadTemplateImage;
  templateId: string;
  imageContextOptions: { key: string; label: string; category: string }[];
  isLast: boolean;
  onPreview: (image: ReadTemplateImage) => void;
}) => {
  const [remap] = useTemplateImageRemapMutation();

  const handleContextKeyChange = async (value: string) => {
    const newKey = value === NONE_VALUE ? null : value;
    try {
      await remap({
        id: templateId,
        imageId: image.id as string,
        patchedUpdateTemplateImage: { context_key: newKey },
      }).unwrap();
      toast.success(
        newKey
          ? `Mapped to ${imageContextOptions.find((o) => o.key === newKey)?.label ?? newKey}`
          : 'Mapping cleared',
      );
    } catch {
      toast.error('Failed to update image mapping');
    }
  };

  const filename = image.rel_path.split('/').pop() ?? image.rel_path;

  return (
    <div className={isLast ? '' : 'border-b border-zinc-100 dark:border-zinc-800'}>
      <div className="flex items-center gap-2.5 py-1.5">
        {/* Thumbnail — clickable */}
        <button
          type="button"
          onClick={() => onPreview(image)}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-zinc-100 transition-opacity hover:opacity-75 dark:bg-zinc-800"
        >
          {image.file_url ? (
            <img
              src={image.file_url}
              alt={filename}
              className="h-9 w-9 cursor-pointer rounded object-contain"
            />
          ) : (
            <ImageIcon className="h-3.5 w-3.5 text-zinc-400" />
          )}
        </button>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <p className="truncate font-mono text-[11px] font-medium leading-tight text-zinc-900 dark:text-zinc-100">
            {filename}
          </p>
          <p className="text-[10px] leading-tight text-zinc-500">
            {image.width}&times;{image.height} &middot; {formatBytes(image.size_bytes)}
          </p>
        </div>

        {/* Mapping dropdown */}
        <Select
          value={image.context_key ?? NONE_VALUE}
          onValueChange={handleContextKeyChange}
        >
          <SelectTrigger className="h-6 w-36 shrink-0 text-[11px]">
            <SelectValue placeholder="No mapping" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>
              <span className="text-zinc-500">No mapping</span>
            </SelectItem>
            {imageContextOptions.map((opt) => (
              <SelectItem key={opt.key} value={opt.key}>
                <span className="flex items-center gap-1.5">
                  <span>{opt.label}</span>
                  <span className="text-[10px] font-medium text-zinc-500">{opt.category}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Loading / Empty
// ---------------------------------------------------------------------------

const LoadingSkeleton = () => (
  <div className="space-y-1.5">
    {Array.from({ length: 2 }).map((_, i) => (
      <div
        key={i}
        className="h-11 animate-pulse rounded bg-zinc-50 dark:bg-zinc-900"
      />
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TemplateImagesPanel = ({
  templateId,
}: TemplateImagesPanelProps) => {
  const { data: images, isLoading, error } = useTemplateImagesQuery({ id: templateId });
  const { data: contextFieldsData } = useTemplateContextFieldsQuery();
  const [previewImage, setPreviewImage] = useState<ReadTemplateImage | null>(null);

  // Derive image-type context field options from the context fields endpoint
  const imageContextOptions = useMemo(() => {
    if (!contextFieldsData?.categories) return [];
    return contextFieldsData.categories.flatMap((cat) =>
      cat.fields
        .filter((field) => field.type === 'image' && !EXCLUDED_IMAGE_KEYS.has(field.key))
        .map((field) => ({
          key: field.key,
          label: FRIENDLY_IMAGE_LABELS[field.key] ?? field.label,
          category: cat.label,
        })),
    );
  }, [contextFieldsData]);

  if (isLoading) return <LoadingSkeleton />;

  if (error) {
    return (
      <div className="rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-700 dark:bg-red-900/10 dark:text-red-400">
        Failed to load images.
      </div>
    );
  }

  const imageList = (images ?? []) as ReadTemplateImage[];

  if (imageList.length === 0) {
    return (
      <p className="py-3 text-center text-xs font-medium text-zinc-500">
        No embedded images
      </p>
    );
  }

  return (
    <>
      <div>
        {imageList.map((image, idx) => (
          <ImageItem
            key={image.id}
            image={image}
            templateId={templateId}
            imageContextOptions={imageContextOptions}
            isLast={idx === imageList.length - 1}
            onPreview={setPreviewImage}
          />
        ))}
      </div>

      <ImageLightbox
        image={previewImage}
        open={!!previewImage}
        onOpenChange={(open) => !open && setPreviewImage(null)}
      />
    </>
  );
};
